export type CommercialDocumentKind = 'invoice' | 'quote';

export const DEFAULT_INVOICE_NUMBER_PREFIX = 'INV-';
export const DEFAULT_QUOTE_NUMBER_PREFIX = 'Q-';
export const DEFAULT_DOCUMENT_NUMBER_PADDING = 4;

export function normalizeDocumentNumberPrefix(value: unknown, fallback: string): string {
    const normalized = String(value || '')
        .trim()
        .replace(/[^a-z0-9 _./-]/gi, '')
        .slice(0, 24);
    return normalized || fallback;
}

export function normalizeDocumentNumberPadding(value: unknown): number {
    const parsed = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(parsed) ? Math.min(8, Math.max(2, parsed)) : DEFAULT_DOCUMENT_NUMBER_PADDING;
}

export function formatCommercialDocumentNumber(prefix: string, sequence: number, padding: number): string {
    const safeSequence = Math.max(1, Math.trunc(sequence));
    return `${prefix}${String(safeSequence).padStart(normalizeDocumentNumberPadding(padding), '0')}`;
}

export function nextCommercialDocumentSequence(
    numbers: unknown[],
    prefix: string,
    minimumSequence = 1,
): number {
    const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escapedPrefix}(\\d+)$`, 'i');
    const highestExistingSequence = numbers.reduce((maximum, value) => {
        const match = String(value || '').trim().match(pattern);
        const sequence = match ? Number.parseInt(match[1] || '', 10) : 0;
        return Number.isSafeInteger(sequence) ? Math.max(maximum, sequence) : maximum;
    }, 0);

    return Math.max(1, Math.trunc(minimumSequence), highestExistingSequence + 1);
}

export async function requestNextCommercialDocumentNumber(kind: CommercialDocumentKind, clientId?: string): Promise<string> {
    const tenantStore = useTenantStore();
    const { $auth } = useNuxtApp();
    const tenantId = String(tenantStore.currentTenantId || '').trim();
    const authUser = $auth.currentUser;

    if (!tenantId || !authUser) {
        throw new Error('A signed-in workspace is required to generate a document number.');
    }

    const response = await $fetch<{ number: string }>('/api/commercial-documents/next-number', {
        method: 'POST',
        headers: {
            authorization: `Bearer ${await authUser.getIdToken()}`,
        },
        body: { tenantId, kind, clientId },
    });

    return response.number;
}

export async function assignCommercialDocumentNumber(
    kind: CommercialDocumentKind,
    documentId: string,
    number: string,
): Promise<string> {
    const tenantStore = useTenantStore();
    const { $auth } = useNuxtApp();
    const tenantId = String(tenantStore.currentTenantId || '').trim();
    const authUser = $auth.currentUser;
    if (!tenantId || !authUser) {
        throw new Error('A signed-in workspace is required to assign a document number.');
    }
    const response = await $fetch<{ number: string }>('/api/commercial-documents/set-number', {
        method: 'POST',
        headers: { authorization: `Bearer ${await authUser.getIdToken()}` },
        body: { tenantId, kind, documentId, number },
    });
    return response.number;
}

export async function reconcileMissingCommercialDocumentNumbers(
    kind: CommercialDocumentKind,
): Promise<Array<{ id: string; number: string }>> {
    const tenantStore = useTenantStore();
    const { $auth } = useNuxtApp();
    const tenantId = String(tenantStore.currentTenantId || '').trim();
    const authUser = $auth.currentUser;
    if (!tenantId || !authUser) return [];
    const response = await $fetch<{ assigned: Array<{ id: string; number: string }> }>('/api/commercial-documents/reconcile-numbers', {
        method: 'POST',
        headers: { authorization: `Bearer ${await authUser.getIdToken()}` },
        body: { tenantId, kind },
    });
    return response.assigned;
}
